import React, { useEffect, useRef, useState } from 'react'
import { getAudioVolume } from '../../utils/CookiesUtils'
import './AccumuladoOverlay.css'

// "ACUMULADO" jackpot win, timed off public/ice-assets/jakpot-video.mp4
// (also youtube fiiyOI8GPGU). t=0 is when the A shows at the top edge:
//   - no dim, everything runs straight over the lit reels
//   - each letter slides straight down from above the screen to its place at
//     a steady ~270 stage px/s. A lands ~2.7s, then one every ~0.47s, O ~6.5s.
//     After that the word stands still.
//   - coins rain top to bottom, spinning, from ~0.8s until the win is paid
//
// Every piece is cut from the machine's own frames (art-source/accumulated/)
// by scripts/build-accumulado-sprites.py. The frames can't simply be flipped
// through: they have two frames per letter fall, and their loop spins the
// coins in place. So the pieces are moved here with CSS keyframes.

const SPRITES = `${process.env.PUBLIC_URL}/ice-assets/accumulated-sprites`

// left, top, width, height in the 1024x768 stage the frames were drawn on
const LETTERS = [
    [29, 519, 195, 239],
    [212, 425, 133, 184],
    [351, 348, 124, 188],
    [487, 280, 128, 197],
    [620, 223, 91, 161],
    [710, 193, 71, 146],
    [775, 150, 85, 151],
    [857, 115, 74, 133],
    [927, 76, 75, 121],
]
// coin_N.png: 13-cell spin strip, cell size in stage px
const COINS = [107, 121, 76, 82, 91, 106, 118, 95, 61, 63, 66, 116, 69, 56, 69, 62, 73, 56, 81]

const STAGE_W = 1024
const STAGE_H = 768
const FALL_SPEED = 270 // stage px per second
const FIRST_LAND = 2.8 // s
const LAND_GAP = 0.475 // s between landings

const COIN_START = 800 // ms
const COIN_EVERY = 90 // ms, ~30 on screen at once, as in the video

const urls = [
    ...LETTERS.map((_, i) => `${SPRITES}/letter_${i}.png`),
    ...COINS.map((_, i) => `${SPRITES}/coin_${i}.png`),
]

function AccumuladoOverlay() {
    const [ready, setReady] = useState(false)
    const coinLayerRef = useRef(null)

    // everything decoded before t=0, so the first letters don't stall
    useEffect(() => {
        let cancelled = false
        Promise.all(urls.map((src) => {
            const img = new Image()
            img.src = src
            return img.decode().catch(() => { })
        })).then(() => { if (!cancelled) setReady(true) })
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (!ready) return
        const sfx = new Audio('assets/ice-bonus/acumulado.wav')
        sfx.volume = getAudioVolume()
        sfx.loop = true
        sfx.play().catch(() => { })

        const layer = coinLayerRef.current
        const dropCoin = () => {
            if (!layer) return
            const k = Math.floor(Math.random() * COINS.length)
            const size = (COINS[k] / STAGE_H) * 100 // vh, the coins stay round on any screen
            const coin = document.createElement('div')
            coin.className = 'accumulado-coin'
            coin.style.backgroundImage = `url(${SPRITES}/coin_${k}.png)`
            coin.style.width = `${size}vh`
            coin.style.height = `${size}vh`
            coin.style.left = `calc(${Math.random() * 100}% - ${size / 2}vh)`
            coin.style.zIndex = Math.random() < 0.5 ? 1 : 3 // some behind the word, some in front
            coin.style.animationDuration = `${2 + Math.random() * 1.5}s, ${0.8 + Math.random() * 0.6}s`
            coin.style.animationDelay = `0s, -${Math.random() * 1.4}s`
            coin.addEventListener('animationend', () => coin.remove())
            layer.appendChild(coin)
        }
        let iv
        const start = setTimeout(() => {
            dropCoin()
            iv = setInterval(dropCoin, COIN_EVERY)
        }, COIN_START)

        return () => {
            clearTimeout(start)
            clearInterval(iv)
            sfx.pause()
            sfx.currentTime = 0
        }
    }, [ready])

    if (!ready) return null

    return (
        <div className='accumulado-overlay' aria-hidden='true'>
            <div className='accumulado-coins' ref={coinLayerRef} />
            {LETTERS.map(([x, y, w, h], i) => {
                const fallFor = (y + h) / FALL_SPEED // from just above the top edge
                const landAt = FIRST_LAND + LAND_GAP * i
                return (
                    <img
                        key={i}
                        className='accumulado-letter'
                        src={`${SPRITES}/letter_${i}.png`}
                        alt=''
                        style={{
                            left: `${(x / STAGE_W) * 100}%`,
                            top: `${(y / STAGE_H) * 100}%`,
                            width: `${(w / STAGE_W) * 100}%`,
                            height: `${(h / STAGE_H) * 100}%`,
                            '--acm-from': `${-((y + h) / STAGE_H) * 100}vh`,
                            animationDuration: `${fallFor}s`,
                            animationDelay: `${landAt - fallFor}s`,
                        }}
                    />
                )
            })}
        </div>
    )
}

export default AccumuladoOverlay
